/**
 * Base Models composable for the Model Gateway feature
 * Provides a convenient interface for components to interact with base models
 */

import { ref, computed } from 'vue';
import { useModelGatewayStore } from '@/stores/modelGateway';
import type { Provider, BaseModelQueryParams } from '../types';

/**
 * Composable for managing base models with filtering capabilities
 * Provides provider filter, enabled-only filter, and filtered models computed property
 * Exposes store actions for fetching, enabling, and disabling base models
 */
export function useBaseModels() {
  const store = useModelGatewayStore();

  // Filter state
  const providerFilter = ref<Provider | 'all'>('all');
  const showEnabledOnly = ref(false);

  /**
   * Computed property that filters base models based on active filters
   * Applies provider filter and enabled-only filter
   */
  const filteredModels = computed(() => {
    let models = store.baseModels;

    // Apply provider filter
    if (providerFilter.value !== 'all') {
      models = models.filter(model => model.provider === providerFilter.value);
    }

    // Apply enabled-only filter
    if (showEnabledOnly.value) {
      models = models.filter(model => model.is_enabled_for_org);
    }

    return models;
  });

  /**
   * Fetch base models with optional filtering
   * @param params - Optional query parameters for filtering
   */
  const fetchBaseModels = async (params?: BaseModelQueryParams): Promise<void> => {
    await store.fetchBaseModels(params);
  };

  /**
   * Add a base model to the organization with optional custom name
   * @param slug - The unique identifier for the base model
   * @param name - Optional custom name for the model
   * @returns Promise that resolves to true if successful, false otherwise
   */
  const addBaseModel = async (slug: string, name?: string): Promise<boolean> => {
    return await store.addBaseModel(slug, name);
  };

  /**
   * Enable a base model for the organization
   * @param name - The name of the base model (custom name or slug)
   * @returns Promise that resolves to true if successful, false otherwise
   */
  const enableBaseModel = async (name: string): Promise<boolean> => {
    return await store.enableBaseModel(name);
  };

  /**
   * Disable a base model for the organization
   * @param name - The name of the base model (custom name or slug)
   * @returns Promise that resolves to true if successful, false otherwise
   */
  const disableBaseModel = async (name: string): Promise<boolean> => {
    return await store.disableBaseModel(name);
  };

  /**
   * Remove a base model from the organization
   * @param name - The name of the base model (custom name or slug)
   * @returns Promise that resolves to true if successful, false otherwise
   */
  const deleteBaseModel = async (name: string): Promise<boolean> => {
    return await store.deleteBaseModel(name);
  };

  return {
    // State - all base models from store
    baseModels: computed(() => store.baseModels),

    // Filtered models based on active filters
    filteredModels,

    // Enabled models only
    enabledModels: computed(() => store.enabledBaseModels),

    // Loading and error state
    isLoading: computed(() => store.isLoadingBaseModels),
    error: computed(() => store.baseModelsError),

    // Filter state
    providerFilter,
    showEnabledOnly,

    // Actions
    fetchBaseModels,
    addBaseModel,
    enableBaseModel,
    disableBaseModel,
    deleteBaseModel,
  };
}
